import { FC, useEffect, useState } from 'react'
import classNames from 'classnames'
import { BaseModal } from 'src/containers/BaseModal'
import { ALL_VARIATION_LANGUAGES } from 'src/constants/variationLanguages'
import styles from './addLanguageModal.module.scss'

interface AddLanguageModalProps {
  isOpen: boolean
  onApply: (languages: string[]) => void
  onClose: () => void
  selected: string[]
}

/** Full language picker for keyword variations (opened from the "+ more" chip). */
export const AddLanguageModal: FC<AddLanguageModalProps> = ({
  isOpen,
  onApply,
  onClose,
  selected,
}) => {
  const [draft, setDraft] = useState<string[]>(selected)

  // Re-sync the draft to the live selection each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setDraft(selected)
    }
  }, [isOpen])

  const allSelected = draft.length === ALL_VARIATION_LANGUAGES.length

  const toggle = (language: string) => {
    setDraft((prev) =>
      prev.includes(language)
        ? prev.filter((item) => item !== language)
        : [...prev, language]
    )
  }

  const toggleAll = () => {
    setDraft(allSelected ? [] : [...ALL_VARIATION_LANGUAGES])
  }

  return (
    <BaseModal
      contentClassName={styles.modal}
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <div className={styles.header}>
        <span className={styles.title}>Add Language</span>
        <button
          aria-label="Close"
          className={styles.close}
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </div>

      <div className={styles.subHeader}>
        <span className={styles.label}>Language</span>
        <label className={styles.selectAll}>
          <input checked={allSelected} onChange={toggleAll} type="checkbox" />
          Select All
        </label>
      </div>

      <div className={styles.grid}>
        {ALL_VARIATION_LANGUAGES.map((language) => (
          <button
            aria-pressed={draft.includes(language)}
            className={classNames(
              styles.chip,
              draft.includes(language) && styles.chipActive
            )}
            key={language}
            onClick={() => toggle(language)}
            type="button"
          >
            {language}
          </button>
        ))}
      </div>

      <div className={styles.footer}>
        <span className={styles.count}>
          <b>{draft.length}</b> {draft.length === 1 ? 'language' : 'languages'}{' '}
          selected
        </span>
        <div className={styles.actions}>
          <button className={styles.cancel} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={styles.add}
            onClick={() => onApply(draft)}
            type="button"
          >
            Add
          </button>
        </div>
      </div>
    </BaseModal>
  )
}

export default AddLanguageModal
